import { useAppRoleDeleteMutation } from "@/common/api/app-role/use-app-role-delete.mutation";
import { useAttributeDeleteMutation } from "@/common/api/attribute/use-attribute-delete.query";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { AppRoleTable, AppRoleType } from "@/components/data-table/predefined/app-role-table";
import { AppRoleTableType } from "@/components/data-table/predefined/app-role-table-columns";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "wouter";

export function AppRoleView() {
  const { t } = useTranslation(["ram", "common"]);
  const [_, navigate] = useLocation();
  const params = useParams<{ applicationUuid: string }>();
  const { mutateAsync: mutateAsyncAppRole } = useAppRoleDeleteMutation();
  const { mutateAsync: mutateAsyncAttribute } = useAttributeDeleteMutation();

  const isV1ManagedAppRole = (appRole: AppRoleTableType): boolean =>
    appRole.v1Id !== null && appRole.v1Id !== undefined;

  const handleAppRoleDelete = async (appRole: AppRoleTableType) => {
    if (isV1ManagedAppRole(appRole)) {
      return;
    }

    await toastMutationStatus({
      mutationFn: () => mutateAsyncAppRole({ uuid: appRole.uuid }),
      successMessage: t("ram:PermissionHasBeenDeleted"),
      errorMessage: t("ram:ErrorInDeletingThePermission"),
    });
  };

  const handleAttributeDelete = async (uuid: string) =>
    await toastMutationStatus({
      mutationFn: () => mutateAsyncAttribute({ uuid }),
      successMessage: t("ram:AttributePermissionHasBeenDeleted"),
      errorMessage: t("ram:ErrorInDeletingTheAttributePermission"),
    });

  const handleRowClick = (appRole: AppRoleTableType) => {
    if (appRole.type === AppRoleType.Protected) return;
    if (!appRole.attribute && appRole.uuid) {
      navigate(`/${appRole.uuid}/update`);
    }
  };

  const handleEdit = (appRole: AppRoleTableType) => {
    if (!appRole.attribute && appRole.uuid) navigate(`/${appRole.uuid}/update`);
  };

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Table}
      title={t("ram:AppRoles")}
      subTitle={t("common:SearchFilterAndManageEntriesDescription")}
    >
      <AppRoleTable
        body={{ application: [params.applicationUuid] }}
        onDelete={handleAppRoleDelete}
        onDeleteAttribute={handleAttributeDelete}
        onRowClick={handleRowClick}
        onRowNavigate={(appRole) => {
          const role = appRole as AppRoleTableType;
          return role.type !== AppRoleType.Protected && !role.attribute && role.uuid
            ? `~/ram/application/${params.applicationUuid}/app-role/${role.uuid}/update`
            : undefined;
        }}
        onEdit={handleEdit}
        showCreateButton={true}
        showAttributeButton={true}
        enableGrouping={true}
      />
    </CenteredLayout>
  );
}
